import { catchError, distinctUntilChanged, filter, map, MonoTypeOperatorFunction, Observable, of, OperatorFunction, shareReplay } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import {
    isEqual as _isEqual
} from 'lodash-es';

import { isPresent } from './type.utils';
import { extractErrorMessageStringFromHttpErrorResponse } from './api.utils';

/**
 * OPERATOR UTILS
 *
 * Utils related to observable operators that can be used in observable pipes
*/

/**
 * Custon RXJS operation that ensures that the object being passed through the observable is not null
 * or undefined. Will output the typing correctly.
 * @returns
 */
export function isDefined<T>(): (source$: Observable<null | undefined | T>) => Observable<T> {
    return (source$: Observable<null | undefined | T>): Observable<T> =>
        source$.pipe(
            filter(isPresent)
        );
}

export function shareReplayRefCount<T>(): MonoTypeOperatorFunction<T> {
    return shareReplay({ bufferSize: 1, refCount: true });
}

/**
 * Meant to be used after a NGXS store dispatch. If the operator returns undefined it means there was no error. If it returns a string it means there was an error.
 * @returns
 */

export function actionDispatchReturnError<T>(): OperatorFunction<T, undefined | string> {
    return (source: Observable<T>) => source.pipe(
        // 1. Map success to a constant value
        map(() => undefined),

        // 2. Handle error gracefully by emitting the error message as a string
        catchError((err: HttpErrorResponse) => {
            const errorMessage = extractErrorMessageStringFromHttpErrorResponse(err);

            // Return an Observable that emits the structured error message and then completes.
            return of(errorMessage);
        })
    );
}

/**
 * Returns a deep distinct until changed that can be used in pipes
 */
export function deepDistinctUntilChanged<T>(): MonoTypeOperatorFunction<T> {
    return distinctUntilChanged((o, n) => _isEqual(o, n));
}
